<script>
    import { onMount } from 'svelte';
    import { get } from '../../../utils.js';
    import { sgettext } from '../../../i18n';
    import { PATH_PREFIX } from '../../../apiUrls.js';
    import { activityVersionId, riskSummaryRoles } from '../diffStore.js';
    import RiskRatingRoleTable from './RiskRatingRoleTable.svelte';
    import RiskTable from './RiskTable.svelte';
    import Spinner from '../../Spinner.svelte';
    import Errors from '../../Errors.svelte';
  
    let resp;
    let roles = [];
    let isLoading = false;
    let errors = null;

    async function getRiskRatingElements() {
      try {
        const response = await get(`${PATH_PREFIX}/sw/api/builder/activity_version_risk_rating_summary/${$activityVersionId}/`);
        if (!response.ok) {
          throw new Error(sgettext('$$FAILED_RETRIEVE_RISK_RATING_ELEMENTS$$'));
        }
        resp = await response.json();
        roles = resp.roles;
        riskSummaryRoles.set(resp.roles)
      } catch (error) {
        errors = error.message;
      }
    }

    function calculateRowSpans(inputRoles) {
        return inputRoles.map(role => {
            let roleSpanCount = 0;
            const validWorkSequences = role.worksequences.reduce((validSequences, worksequence) => {
                const validInstances = worksequence.element_instances.reduce((instancesWithRisks, instance) => {
                const risksAndControlsCount = instance.risks_and_controls.length;
                if (risksAndControlsCount > 0) {
                    const newInstance = { ...instance, elementRowSpan: risksAndControlsCount };
                    const configMap = new Map(newInstance.control_configurations.map(c => [`${c.global_hazard}-${c.global_control}`, c]));
                    newInstance.risks_and_controls = newInstance.risks_and_controls.map(risk => {
                    const key = `${risk.global_hazard?.id}-${risk.global_control?.id}`;
                    const config = configMap.get(key);
                    if (config) {
                        return { ...risk, 
                        severity: config.severity,
                        likelihood: config.likelihood,
                        rrr: config.contextual_residual_risk_rating,
                        confirmed: config.confirmed,
                        };
                    }
                    return risk;
                    });
                    instancesWithRisks.push(newInstance);
                }
                return instancesWithRisks;
                }, []);

                if (validInstances.length > 0) {
                    const updatedWorksequence = {
                        ...worksequence,
                        element_instances: validInstances,
                        rowSpan: validInstances.reduce((sum, { elementRowSpan }) => sum + elementRowSpan, 0),
                    };
                    validSequences.push(updatedWorksequence);
                    roleSpanCount += updatedWorksequence.rowSpan;
                }

                return validSequences;
            }, []);

            if (validWorkSequences.length > 0) {
                return {
                    ...role,
                    worksequences: validWorkSequences,
                    rowSpan: roleSpanCount,
                    activityVersionId: $activityVersionId,
                };
            }
            return null;
        }).filter(role => role !== null);
  }

  onMount(async () => {
    let loadedRoles;
    isLoading = true;
    if ($riskSummaryRoles.length > 0) {
      loadedRoles = $riskSummaryRoles;
    } else {
      await getRiskRatingElements();
      loadedRoles = roles;
    }
    roles = calculateRowSpans(loadedRoles);
    isLoading = false;
  });


</script>

<div class="header-container">
  <h4 class="mb-4">
    {sgettext('$$RISK_CONTROLS_DEFINED_WITHIN_ELEMENT$$')}
  </h4>
</div>

<div class="alert alert-warning">
  <span style="font-size: 1.2rem; cursor: default;"
    ><i class="fa fa-warning mr-2" /></span
  >
  {sgettext(
    '$$PLEASE_NOTE_AMENDMENTS_RISK_CONTROL_NOT$$'
  )}
</div>

{#if errors}
  <Errors {errors} />
{/if}

{#if isLoading}
  <div class="spinner-wrapper">
    <Spinner size="large" />
  </div>
{:else}
  {#each roles as { name, worksequences, rowSpan, activityVersionId }, roleIndex}
    {#if worksequences.length > 0}
      <RiskTable {name} {roleIndex} {worksequences}>
        <RiskRatingRoleTable
          {name}
          {worksequences}
          {roleIndex}
          {rowSpan}
          {activityVersionId}
        />
      </RiskTable>
    {/if}
  {/each}
{/if}

<style>
  .header-container {
    text-align: left;
    margin-bottom: 20px;
  }

  h4.mb-4 {
    margin-bottom: 1rem;
  }

  .spinner-wrapper {
    display: grid;
    place-items: center;
    height: 100px;
  }
</style>
